import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Search as SearchIcon } from '@mui/icons-material';

const EntitySelectionModal = ({
  isOpen,
  title,
  description,
  options,
  selectedValue,
  selectedLabel,
  searchPlaceholder,
  emptyMessage,
  confirmLabel,
  isDarkMode,
  canClose,
  onClose,
  onSelect
}) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');

    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 10);

    return () => window.clearTimeout(timeoutId);
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options.slice(0, 50);

    return options
      .filter(option =>
        String(option?.searchText || option?.label || '')
          .toLowerCase()
          .includes(normalizedQuery)
      )
      .slice(0, 50);
  }, [options, query]);

  const handleRequestClose = () => {
    if (!canClose) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div
        className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm"
        onClick={handleRequestClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative z-10 w-full max-w-2xl rounded-2xl border shadow-2xl ${
            isDarkMode
              ? 'border-slate-700 bg-slate-950 text-slate-100'
              : 'border-slate-200 bg-white text-slate-900'
          }`}
        >
          <div className="flex items-start justify-between gap-4 border-b border-inherit px-5 py-4 sm:px-6">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold">{title}</h3>
              {description ? (
                <p
                  className={`mt-1 text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  {description}
                </p>
              ) : null}
            </div>
            {canClose ? (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close selector"
                className={`rounded-md px-2 py-1 text-sm transition-colors ${
                  isDarkMode
                    ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                    : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}
              >
                Close
              </button>
            ) : null}
          </div>

          <div className="px-5 py-4 sm:px-6">
            <div
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-900'
                  : 'border-slate-200 bg-slate-50'
              }`}
            >
              <SearchIcon
                className={`!h-4 !w-4 ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={event => setQuery(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && filteredOptions[0]) {
                    event.preventDefault();
                    onSelect(filteredOptions[0]);
                  }
                  if (event.key === 'Escape' && canClose) {
                    event.preventDefault();
                    onClose();
                  }
                }}
                placeholder={searchPlaceholder}
                className={`w-full bg-transparent text-sm outline-none ${
                  isDarkMode
                    ? 'text-slate-100 placeholder:text-slate-500'
                    : 'text-slate-700 placeholder:text-slate-400'
                }`}
              />
            </div>

            {selectedLabel ? (
              <p
                className={`mt-3 text-xs ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-500'
                }`}
              >
                Current selection:{' '}
                <span className="font-semibold">{selectedLabel}</span>
              </p>
            ) : null}

            <div
              className={`mt-4 max-h-[26rem] overflow-y-auto rounded-xl border ${
                isDarkMode
                  ? 'border-slate-800 bg-slate-900/70'
                  : 'border-slate-200 bg-white'
              }`}
            >
              {filteredOptions.length ? (
                filteredOptions.map(option => {
                  const isSelected = option?.value === selectedValue;
                  const badges = Array.isArray(option?.badges)
                    ? option.badges.filter(Boolean)
                    : [];

                  return (
                    <button
                      key={option?.value || option?.label}
                      type="button"
                      onClick={() => onSelect(option)}
                      className={`flex w-full items-center justify-between gap-3 border-b px-4 py-3 text-left text-sm transition-colors last:border-b-0 ${
                        isDarkMode ? 'border-slate-800' : 'border-slate-100'
                      } ${
                        isSelected
                          ? 'bg-primary/10 font-semibold text-primary'
                          : isDarkMode
                            ? 'text-slate-200 hover:bg-slate-800'
                            : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-0 truncate">
                            {option?.label}
                          </span>
                          <div className="flex min-w-0 items-center justify-end gap-3">
                            {badges.length ? (
                              <span
                                className={`truncate text-right text-xs font-medium ${
                                  isDarkMode
                                    ? 'text-slate-400'
                                    : 'text-slate-500'
                                }`}
                              >
                                {badges
                                  .slice(0, 6)
                                  .map(badge => `#${badge}`)
                                  .join(' ')}
                              </span>
                            ) : null}
                            {isSelected ? (
                              <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide">
                                Selected
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center">
                  <p
                    className={`text-sm ${
                      isDarkMode ? 'text-slate-400' : 'text-slate-500'
                    }`}
                  >
                    {emptyMessage}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div
            className={`flex items-center justify-between gap-3 border-t px-5 py-4 sm:px-6 ${
              isDarkMode ? 'border-slate-800' : 'border-slate-200'
            }`}
          >
            <p
              className={`text-xs ${
                isDarkMode ? 'text-slate-500' : 'text-slate-500'
              }`}
            >
              {confirmLabel}
            </p>
            {canClose ? (
              <button
                type="button"
                onClick={onClose}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  isDarkMode
                    ? 'border-slate-700 text-slate-200 hover:bg-slate-800'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                Done
              </button>
            ) : (
              <span
                className={`text-xs font-medium ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Selection required
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EntitySelectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ),
  selectedValue: PropTypes.string,
  selectedLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  confirmLabel: PropTypes.string,
  isDarkMode: PropTypes.bool,
  canClose: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

EntitySelectionModal.defaultProps = {
  description: '',
  options: [],
  selectedValue: '',
  selectedLabel: '',
  searchPlaceholder: 'Search...',
  emptyMessage: 'No results found.',
  confirmLabel: 'Choose an item to load the report.',
  isDarkMode: false,
  canClose: true
};

export default EntitySelectionModal;
